import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonTable,
  useConfirm,
  useToast,
} from '@aurora/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../api/client.js';

interface FieldDef {
  id: string;
  entityType: string;
  key: string;
  label: string;
  fieldType: 'text' | 'number' | 'date' | 'select' | 'checkbox';
  options: string[];
  required: boolean;
  sortOrder: number;
}

const ENTITY_TYPES = [
  { value: 'crm.contact', label: 'CRM · Kontakte' },
  { value: 'crm.company', label: 'CRM · Firmen' },
  { value: 'crm.deal', label: 'CRM · Deals' },
  { value: 'projects.task', label: 'Projekte · Aufgaben' },
  { value: 'projects.project', label: 'Projekte · Projekte' },
  { value: 'catalog.item', label: 'Artikel & Leistungen' },
  { value: 'sales.quote', label: 'Verkauf · Angebote' },
  { value: 'accounting.invoice', label: 'Buchhaltung · Rechnungen' },
];

const TYPE_LABELS: Record<FieldDef['fieldType'], string> = {
  text: 'Text',
  number: 'Zahl',
  date: 'Datum',
  select: 'Auswahl',
  checkbox: 'Ja/Nein',
};

const emptyForm = {
  label: '',
  fieldType: 'text' as FieldDef['fieldType'],
  options: '',
  required: false,
};

export default function CustomFieldsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const [entityType, setEntityType] = useState(ENTITY_TYPES[0]!.value);
  const [form, setForm] = useState(emptyForm);

  const defs = useQuery({
    queryKey: ['custom-fields', 'defs', entityType],
    queryFn: () =>
      api<{ defs: FieldDef[] }>(
        `/api/v1/custom-fields/defs?entityType=${encodeURIComponent(entityType)}`,
      ),
  });

  const createDef = useMutation({
    mutationFn: () =>
      api('/api/v1/custom-fields/defs', {
        method: 'POST',
        body: {
          entityType,
          label: form.label,
          fieldType: form.fieldType,
          required: form.required,
          options:
            form.fieldType === 'select'
              ? form.options
                  .split(',')
                  .map((option) => option.trim())
                  .filter(Boolean)
              : [],
        },
      }),
    onSuccess: () => {
      setForm(emptyForm);
      toast.success('Feld angelegt');
      void queryClient.invalidateQueries({ queryKey: ['custom-fields', 'defs', entityType] });
    },
    onError: (error) => toast.error(error.message),
  });

  const removeDef = useMutation({
    mutationFn: (id: string) => api(`/api/v1/custom-fields/defs/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Feld gelöscht');
      void queryClient.invalidateQueries({ queryKey: ['custom-fields', 'defs', entityType] });
    },
    onError: (error) => toast.error(error.message),
  });

  const items = defs.data?.defs ?? [];

  return (
    <div className="page">
      <h1 className="page__title">Benutzerdefinierte Felder</h1>
      <p className="page__lead">
        Ergänze eigene Felder pro Datentyp — sie erscheinen automatisch auf den Detailseiten.
      </p>

      <Field label="Datentyp">
        {(id) => (
          <Select id={id} value={entityType} onChange={(e) => setEntityType(e.target.value)}>
            {ENTITY_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Card>
        {defs.isLoading ? (
          <SkeletonTable rows={4} />
        ) : items.length === 0 ? (
          <EmptyState title="Keine Felder" description="Lege unten das erste eigene Feld an." />
        ) : (
          <table className="au-table">
            <thead>
              <tr>
                <th>Feld</th>
                <th>Typ</th>
                <th>Pflicht</th>
                <th aria-label="Aktionen" />
              </tr>
            </thead>
            <tbody>
              {items.map((def) => (
                <tr key={def.id}>
                  <td>
                    <strong>{def.label}</strong>
                    <span style={{ color: 'var(--au-text-muted)' }}> · {def.key}</span>
                    {def.fieldType === 'select' && def.options.length > 0 ? (
                      <div style={{ color: 'var(--au-text-muted)', fontSize: 'var(--au-text-xs)' }}>
                        {def.options.join(', ')}
                      </div>
                    ) : null}
                  </td>
                  <td>{TYPE_LABELS[def.fieldType]}</td>
                  <td>{def.required ? <Badge tone="accent">Pflicht</Badge> : '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={() => {
                        void confirmDialog({
                          title: 'Feld löschen?',
                          description: `„${def.label}" und alle gespeicherten Werte gehen verloren.`,
                          confirmLabel: 'Löschen',
                          danger: true,
                        }).then((ok) => {
                          if (ok) removeDef.mutate(def.id);
                        });
                      }}
                    >
                      Löschen
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <form
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--au-space-3)' }}
          onSubmit={(event) => {
            event.preventDefault();
            if (form.label.trim()) createDef.mutate();
          }}
        >
          <h2 style={{ fontSize: 'var(--au-text-lg)' }}>Neues Feld</h2>
          <div
            style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--au-space-3)' }}
          >
            <Field label="Bezeichnung">
              {(id) => (
                <Input
                  id={id}
                  required
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                />
              )}
            </Field>
            <Field label="Typ">
              {(id) => (
                <Select
                  id={id}
                  value={form.fieldType}
                  onChange={(e) =>
                    setForm({ ...form, fieldType: e.target.value as FieldDef['fieldType'] })
                  }
                >
                  <option value="text">Text</option>
                  <option value="number">Zahl</option>
                  <option value="date">Datum</option>
                  <option value="select">Auswahl</option>
                  <option value="checkbox">Ja/Nein</option>
                </Select>
              )}
            </Field>
          </div>
          {form.fieldType === 'select' ? (
            <Field label="Optionen (kommagetrennt)">
              {(id) => (
                <Input
                  id={id}
                  placeholder="z. B. Klein, Mittel, Groß"
                  value={form.options}
                  onChange={(e) => setForm({ ...form, options: e.target.value })}
                />
              )}
            </Field>
          ) : null}
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--au-space-2)' }}>
            <input
              type="checkbox"
              checked={form.required}
              onChange={(e) => setForm({ ...form, required: e.target.checked })}
            />
            Pflichtfeld
          </label>
          <div>
            <Button type="submit" loading={createDef.isPending}>
              Feld hinzufügen
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
